import { Link, useParams } from "react-router-dom";
import { useShare } from "@/hooks/useShare";
import { newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { share, status } = useShare();

  const article = newsList.find((item) => item.id === id);

  if (!article) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-4xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">新闻内容不存在</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-500">
          这条资讯可能已被移除或链接失效，你可以返回资讯列表浏览最新内容。
        </p>
        <Link
          to="/news"
          className="mt-7 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="whitespace-nowrap">返回资讯列表</span>
        </Link>
      </div>
    );
  }

  const related = newsList
    .filter((item) => item.id !== article.id)
    .map((item) => {
      let weight = 0;
      if (item.category === article.category) weight += 3;
      weight += item.tags.filter((tag) => article.tags.includes(tag)).length;
      return { item, weight };
    })
    .sort((a, b) => b.weight - a.weight || b.item.date.localeCompare(a.item.date))
    .slice(0, 3)
    .map((entry) => entry.item);

  const sorted = [...newsList].sort((a, b) => b.date.localeCompare(a.date));
  const currentIndex = sorted.findIndex((item) => item.id === article.id);
  const prevArticle = currentIndex > 0 ? sorted[currentIndex - 1] : null;
  const nextArticle = currentIndex < sorted.length - 1 ? sorted[currentIndex + 1] : null;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="transition-colors hover:text-primary-600">
          短剧资讯
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="line-clamp-1 font-medium text-foreground-700">{article.title}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <article className="lg:col-span-8">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded bg-accent-100 px-2.5 py-1 font-medium text-accent-800">
              {article.category}
            </span>
            <span className="flex items-center gap-1 text-foreground-400">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1 text-foreground-400">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>

          <p className="mt-4 border-l-2 border-accent-500 pl-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            {article.summary}
          </p>

          <div className="mt-8 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title} · ai短剧新闻`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8">
            {article.content.map((block, index) => {
              if (block.type === "img" && block.src) {
                return (
                  <figure key={`${article.id}-img-${index}`} className="my-8">
                    <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
                      <img
                        src={block.src}
                        alt={block.caption ?? article.title}
                        title={`${article.title} · ai短剧新闻配图`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    {block.caption && (
                      <figcaption className="mt-3 text-center text-xs text-foreground-400">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              }
              return (
                <p
                  key={`${article.id}-p-${index}`}
                  className="mt-4 text-sm leading-loose text-foreground-700 md:text-base"
                >
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-accent-500 hover:text-foreground-950"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => share(article.title, article.summary, window.location.href)}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-share-forward-line"></i>
                <span className="whitespace-nowrap">分享这篇资讯</span>
              </button>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="whitespace-nowrap">返回列表</span>
              </Link>
            </div>
            {status === "copied" && (
              <span className="text-xs font-medium text-accent-600">链接已复制到剪贴板</span>
            )}
            {status === "failed" && (
              <span className="text-xs font-medium text-accent-600">当前浏览器暂不支持分享</span>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {prevArticle ? (
              <Link
                to={`/news/${prevArticle.id}`}
                className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="text-xs text-foreground-400">
                  <i className="ri-arrow-left-s-line"></i> 上一篇
                </span>
                <p className="mt-1.5 line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-600">
                  {prevArticle.title}
                </p>
              </Link>
            ) : (
              <div className="rounded-lg border border-dashed border-background-200 p-4 text-xs text-foreground-400">
                已经是第一篇资讯
              </div>
            )}

            {nextArticle ? (
              <Link
                to={`/news/${nextArticle.id}`}
                className="group rounded-lg border border-background-200 bg-background-50 p-4 text-right transition-colors hover:border-primary-300"
              >
                <span className="text-xs text-foreground-400">
                  下一篇 <i className="ri-arrow-right-s-line"></i>
                </span>
                <p className="mt-1.5 line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-600">
                  {nextArticle.title}
                </p>
              </Link>
            ) : (
              <div className="rounded-lg border border-dashed border-background-200 p-4 text-right text-xs text-foreground-400">
                已经是最后一篇资讯
              </div>
            )}
          </div>
        </article>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">相关文章推荐</h2>
              <div className="mt-4 flex flex-col divide-y divide-background-200">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex gap-3 py-4 first:pt-0 last:pb-0"
                  >
                    <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} 配图`}
                        title={`${item.title} · ai短剧资讯`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h3>
                      <span className="mt-1 block text-xs text-foreground-400">{item.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}